/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import {
  fetchAiAdminUsage,
  fetchAskEvansAudit,
  formatApiError,
  formatBedrockModelLabel,
  formatRelativeTime,
  formatTokenCount,
  formatUsd,
  periodLabel,
} from '../../services/aiInsightsApi';
import AdminAiSelect from './AdminAiSelect';
import './AdminAiPages.css';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'queued', label: 'Queued' },
  { value: 'fallback', label: 'Fallback' },
];

const ALL_USERS = '__all__';
const QUESTION_PREVIEW = 96;
const ANSWER_PREVIEW = 480;
const DEFAULT_PAGE_SIZE = 25;

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    out.push({ value: key, label: periodLabel(key) });
  }
  return out;
};

const truncate = (text, limit) => {
  const t = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return '';
  return t.length <= limit ? t : `${t.slice(0, limit).trimEnd()}…`;
};

const statusBadge = (status) => {
  const s = String(status || '').toLowerCase();
  let cls = 'admin-ai-badge admin-ai-badge--warn';
  let label = status || '—';
  if (s === 'completed') {
    cls = 'admin-ai-badge admin-ai-badge--ok';
    label = 'Completed';
  } else if (s === 'failed') {
    cls = 'admin-ai-badge admin-ai-badge--err';
    label = 'Failed';
  } else if (s === 'running') label = 'Running';
  else if (s === 'queued') label = 'Queued';
  else if (s === 'fallback') label = 'Fallback';
  return <span className={cls}>• {label}</span>;
};

const barClass = (status) => {
  if (status === 'blocked' || status === 'over') return 'admin-ai-bar__fill--danger';
  if (status === 'warning') return 'admin-ai-bar__fill--warn';
  return 'admin-ai-bar__fill--ok';
};

const BucketCell = ({ usage }) => {
  const pct = Math.min(100, Math.max(0, Number(usage?.percentage) || 0));
  return (
    <div className="admin-ai-bucket">
      <div className="admin-ai-bucket__top">
        <span>
          {formatTokenCount(usage?.used)} of {formatTokenCount(usage?.allowance)}
        </span>
        <span className="admin-ai-muted">{pct}%</span>
      </div>
      <div className="admin-ai-bar">
        <div className={`admin-ai-bar__fill ${barClass(usage?.status)}`} style={{ width: `${pct}%` }} />
      </div>
      {usage?.status && usage.status !== 'ok' ? (
        <span className={`admin-ai-badge admin-ai-badge--${usage.status === 'warning' ? 'warn' : 'err'}`}>
          {usage.status === 'blocked' ? 'Blocked' : usage.status === 'over' ? 'Over' : 'Near limit'}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Admin AI Usage — Allowance + Ask Evans audit (Lessons Learned sister).
 */
const AdminAskEvansAuditPage = () => {
  const months = useMemo(() => monthOptions(), []);
  const [tab, setTab] = useState('allowance');
  const [period, setPeriod] = useState(months[0]?.value || '');

  const [allowanceLoading, setAllowanceLoading] = useState(true);
  const [allowanceError, setAllowanceError] = useState(null);
  const [allowance, setAllowance] = useState({
    users: [],
    totals: { workflowUsed: 0, assistantUsed: 0, cost: null },
    resetDate: '',
    periodStart: '',
  });

  const [status, setStatus] = useState('all');
  const [userKey, setUserKey] = useState(ALL_USERS);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [audit, setAudit] = useState({
    items: [],
    total: 0,
    users: [],
    resetDate: '',
    periodStart: '',
  });
  const [expanded, setExpanded] = useState({});
  const [answerOpen, setAnswerOpen] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  const loadAllowance = useCallback(async () => {
    setAllowanceLoading(true);
    setAllowanceError(null);
    try {
      const res = await fetchAiAdminUsage({ period });
      setAllowance(res);
    } catch (err) {
      setAllowanceError(formatApiError(err, 'Failed to load AI usage'));
      setAllowance((prev) => ({ ...prev, users: [] }));
    } finally {
      setAllowanceLoading(false);
    }
  }, [period]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const isAll = !userKey || userKey === ALL_USERS;
      const res = await fetchAskEvansAudit({
        period,
        status: status === 'all' ? undefined : status,
        userEmail: !isAll && String(userKey).includes('@') ? userKey : undefined,
        userSub: !isAll && !String(userKey).includes('@') ? userKey : undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setAudit(res);
    } catch (err) {
      setAuditError(formatApiError(err, 'Failed to load Ask Evans audit'));
      setAudit((prev) => ({ ...prev, items: [], total: 0 }));
    } finally {
      setAuditLoading(false);
    }
  }, [period, status, userKey, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [period, status, userKey]);

  useEffect(() => {
    loadAllowance();
  }, [loadAllowance]);

  useEffect(() => {
    if (tab !== 'ask-evans') return;
    loadAudit();
  }, [tab, loadAudit]);

  const userOptions = useMemo(
    () => [
      { value: ALL_USERS, label: 'All users' },
      ...(audit.users || []).map((u) => ({
        value: u.userEmail || u.userSub || u.label,
        label: u.label,
      })),
    ],
    [audit.users]
  );

  const totalPages = Math.max(1, Math.ceil((audit.total || 0) / pageSize));
  const periodText = periodLabel(allowance.periodStart || audit.periodStart || period);
  const resetText = periodLabel(allowance.resetDate) || allowance.resetDate;

  return (
    <div className="admin-ai-page admin-ai-page--usage">
      <header className="admin-ai-page__header">
        <div>
          <h2 className="admin-ai-page__title">AI Usage</h2>
          <p className="admin-ai-page__sub">Who is spending the AI allowance, and what it costs</p>
        </div>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={() => (tab === 'allowance' ? loadAllowance() : loadAudit())}
          disabled={tab === 'allowance' ? allowanceLoading : auditLoading}
        >
          Refresh
        </button>
      </header>

      <div className="admin-ai-panel">
        <div className="admin-ai-panel__row">
          <AdminAiSelect
            label="Period"
            className="admin-ai-select-wrap--sm"
            options={months}
            value={period}
            onChange={setPeriod}
            searchable={false}
          />
          {tab === 'allowance' && !allowanceLoading && !allowanceError ? (
            <p className="admin-ai-panel__stat">
              {formatTokenCount(allowance.totals.workflowUsed)} workflow tokens,{' '}
              {formatTokenCount(allowance.totals.assistantUsed)} assistant tokens
              {allowance.totals.cost != null ? `, ${formatUsd(allowance.totals.cost)}` : ', —'} across{' '}
              {allowance.users.length} users
              {resetText ? `. Allowances reset on ${resetText}.` : '.'}
            </p>
          ) : null}
          {tab === 'ask-evans' && !auditLoading && !auditError ? (
            <p className="admin-ai-panel__stat">
              {audit.total} Ask Evans question{audit.total === 1 ? '' : 's'}
              {periodText ? ` in ${periodText}` : ''}
            </p>
          ) : null}
        </div>
      </div>

      <div className="admin-ai-tabs" role="tablist" aria-label="AI Usage views">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'allowance'}
          className={`admin-ai-tab${tab === 'allowance' ? ' admin-ai-tab--active' : ''}`}
          onClick={() => setTab('allowance')}
        >
          Allowance
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'ask-evans'}
          className={`admin-ai-tab${tab === 'ask-evans' ? ' admin-ai-tab--active' : ''}`}
          onClick={() => setTab('ask-evans')}
        >
          Ask Evans audit
        </button>
      </div>

      {tab === 'allowance' ? (
        <>
          {allowanceError ? (
            <div className="admin-ai-banner admin-ai-banner--err">
              <span>{allowanceError}</span>
              <button type="button" className="admin-ai-link" onClick={loadAllowance}>
                Retry
              </button>
            </div>
          ) : null}
          {allowanceLoading ? (
            <div className="admin-ai-page--loading">
              <Spin />
            </div>
          ) : null}
          {!allowanceLoading && !allowanceError && !allowance.users.length ? (
            <div className="admin-ai-empty">Nobody has used an AI feature in this period.</div>
          ) : null}
          {!allowanceLoading && allowance.users.length ? (
            <div className="admin-ai-audit">
              <div className="admin-ai-audit__scroll">
                <table className="admin-ai-audit__table admin-ai-allowance__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Tier</th>
                      <th>Workflow</th>
                      <th>Assistant</th>
                      <th>Cost</th>
                      <th>Last used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowance.users.map((user) => (
                      <tr key={user.userSub || user.email}>
                        <td>
                          <div className="admin-ai-audit__user">{user.name}</div>
                          <div className="admin-ai-audit__user-sub">{user.email || '—'}</div>
                        </td>
                        <td>
                          <span className="admin-ai-pill">• {user.tierLabel}</span>
                        </td>
                        <td>
                          <BucketCell usage={user.workflow} />
                        </td>
                        <td>
                          <BucketCell usage={user.assistant} />
                        </td>
                        <td className="admin-ai-audit__num">{formatUsd(user.cost)}</td>
                        <td className="admin-ai-audit__when">
                          {formatRelativeTime(user.lastUsedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'ask-evans' ? (
        <>
          <div className="admin-ai-panel admin-ai-panel--filters">
            <div className="admin-ai-panel__row">
              <AdminAiSelect
                label="User"
                className="admin-ai-select-wrap--md"
                options={userOptions}
                value={userKey}
                onChange={setUserKey}
                searchable
                disabled={auditLoading}
              />
              <AdminAiSelect
                label="Status"
                className="admin-ai-select-wrap--sm"
                options={STATUS_OPTIONS}
                value={status}
                onChange={setStatus}
                searchable={false}
                disabled={auditLoading}
              />
              <p className="admin-ai-panel__hint">
                Expand a row for the full answer, tools, and Bedrock call breakdown. Cost uses
                Bedrock list prices for the model used.
              </p>
            </div>
          </div>

          {auditError ? (
            <div className="admin-ai-banner admin-ai-banner--err">
              <span>{auditError}</span>
              <button type="button" className="admin-ai-link" onClick={loadAudit}>
                Retry
              </button>
            </div>
          ) : null}

          {auditLoading ? (
            <div className="admin-ai-page--loading">
              <Spin />
            </div>
          ) : null}

          {!auditLoading && !auditError && !audit.items.length ? (
            <div className="admin-ai-empty">
              No Ask Evans questions in this period for the selected filters.
            </div>
          ) : null}

          {!auditLoading && audit.items.length ? (
            <div className="admin-ai-audit">
              <div className="admin-ai-audit__scroll">
                <table className="admin-ai-audit__table">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>When</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Question</th>
                      <th>Model</th>
                      <th>Tokens</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.items.map((item) => {
                      const isOpen = Boolean(expanded[item.jobId]);
                      const email = item.userEmail || item.userName || item.userSub || '—';
                      const hasUsage = item.bedrockCallCount > 0 || item.totalTokens > 0;
                      const answerExpanded = Boolean(answerOpen[item.jobId]);
                      return (
                        <React.Fragment key={item.jobId}>
                          <tr className={isOpen ? 'is-open' : ''}>
                            <td>
                              <button
                                type="button"
                                className="admin-ai-audit__expand"
                                aria-label={isOpen ? 'Hide details' : 'Show details'}
                                onClick={() =>
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [item.jobId]: !prev[item.jobId],
                                  }))
                                }
                              >
                                {isOpen ? <DownOutlined /> : <RightOutlined />}
                              </button>
                            </td>
                            <td className="admin-ai-audit__when">
                              {formatRelativeTime(item.createdAt)}
                            </td>
                            <td>
                              <div className="admin-ai-audit__user" title={email}>
                                {email}
                              </div>
                              {item.userEmail && item.userName ? (
                                <div className="admin-ai-audit__user-sub">{item.userName}</div>
                              ) : null}
                            </td>
                            <td>{statusBadge(item.status)}</td>
                            <td>
                              <p className="admin-ai-audit__q" title={item.question}>
                                {truncate(item.question, QUESTION_PREVIEW) || '—'}
                              </p>
                              {!isOpen && item.answer ? (
                                <p className="admin-ai-audit__a-preview">
                                  {truncate(item.answer, 72)}
                                </p>
                              ) : null}
                            </td>
                            <td>
                              <span className="admin-ai-pill" title={item.modelId || undefined}>
                                {formatBedrockModelLabel(item.modelId)}
                              </span>
                            </td>
                            <td>
                              {hasUsage ? (
                                <div>
                                  <div className="admin-ai-audit__num">
                                    {formatTokenCount(item.totalTokens)}
                                  </div>
                                  <div className="admin-ai-audit__num-sub">
                                    {formatTokenCount(item.inputTokens)}/
                                    {formatTokenCount(item.outputTokens)}
                                  </div>
                                </div>
                              ) : (
                                <span className="admin-ai-muted">—</span>
                              )}
                            </td>
                            <td>
                              {hasUsage ? (
                                <div className="admin-ai-audit__num">{formatUsd(item.cost)}</div>
                              ) : (
                                <span className="admin-ai-muted">—</span>
                              )}
                            </td>
                          </tr>
                          {isOpen ? (
                            <tr className="admin-ai-audit__detail-row">
                              <td colSpan={8}>
                                <div className="admin-ai-audit__detail">
                                  <div className="admin-ai-audit__detail-grid">
                                    <div>
                                      <p className="admin-ai-audit__detail-label">Question</p>
                                      <p className="admin-ai-audit__detail-body">
                                        {item.question || '—'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="admin-ai-audit__detail-label">Answer</p>
                                      {item.status === 'failed' && item.error ? (
                                        <p className="admin-ai-audit__detail-err">{item.error}</p>
                                      ) : item.answer ? (
                                        <div className="admin-ai-audit__detail-body">
                                          {answerExpanded || item.answer.length <= ANSWER_PREVIEW
                                            ? item.answer
                                            : `${item.answer.slice(0, ANSWER_PREVIEW).trimEnd()}…`}
                                          {item.answer.length > ANSWER_PREVIEW ? (
                                            <button
                                              type="button"
                                              className="admin-ai-link"
                                              onClick={() =>
                                                setAnswerOpen((prev) => ({
                                                  ...prev,
                                                  [item.jobId]: !prev[item.jobId],
                                                }))
                                              }
                                            >
                                              {answerExpanded ? 'Show less' : 'Read more'}
                                            </button>
                                          ) : null}
                                        </div>
                                      ) : (
                                        <p className="admin-ai-muted">—</p>
                                      )}
                                    </div>
                                  </div>
                                  {item.modelId ? (
                                    <p className="admin-ai-muted" title={item.modelId}>
                                      {item.modelId}
                                    </p>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {audit.total > pageSize ? (
                <div className="admin-ai-pager">
                  <button
                    type="button"
                    className="admin-ai-btn admin-ai-btn--ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="admin-ai-pager__label">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="admin-ai-btn admin-ai-btn--ghost"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default AdminAskEvansAuditPage;
